import { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Building, Phone, MapPin, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    company_name: user?.company_name || '',
    contact_name: user?.contact_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    postal_code: user?.postal_code || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile(formData);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Mon Profil</h1>
        <p className="text-white/50 mt-1">Gérez vos informations personnelles</p>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="card-dashboard space-y-6"
      >
        {/* Account Info */}
        <div className="pb-6 border-b border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Informations du compte</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Email</label>
              <Input
                type="email"
                value={user?.email || ''}
                disabled
                className="bg-[#0A0A0A] border-white/10 text-white/50 rounded-none"
              />
              <p className="text-white/30 text-xs mt-1">L'email ne peut pas être modifié</p>
            </div>
            <div>
              <label className="text-white/60 text-sm mb-2 block">Rôle</label>
              <Input
                value={user?.role === 'admin' ? 'Administrateur' : 'Professionnel'}
                disabled
                className="bg-[#0A0A0A] border-white/10 text-white/50 rounded-none"
              />
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="pb-6 border-b border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Entreprise</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Nom de l'entreprise *</label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                data-testid="profile-company"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm mb-2 block">Nom du contact *</label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                data-testid="profile-contact"
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="pb-6 border-b border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Contact</h2>
          </div>
          
          <div>
            <label className="text-white/60 text-sm mb-2 block">Téléphone *</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              required
              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
              data-testid="profile-phone"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Adresse</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Adresse</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                data-testid="profile-address"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm mb-2 block">Ville</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                  data-testid="profile-city"
                />
              </div>
              <div>
                <label className="text-white/60 text-sm mb-2 block">Code postal</label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                  data-testid="profile-postal"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center gap-2"
            data-testid="profile-save"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" />
                Enregistrer les modifications
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
}
